/**
 * collectSession.ts —— 「刷新一下不用重新收验证码」。
 *
 * 换来的 collectToken 原先只活在 React state 里，刷新即丢，于是又要发一封验证码 ——
 * 而服务端那个令牌其实还好好的。这里把它在浏览器里存一小会儿。
 *
 * 🔴 **用 sessionStorage，不用 localStorage。** 这条链接会被转发，也可能在共用的电脑上
 * 打开。sessionStorage 关掉标签页就没了，恰好只解决「刷新」这一件事，不会在别人的机器上
 * 留下一个能直接填银行信息的凭据。
 *
 * 🔴 **只存令牌，不存已填的表单内容。** 那里面是银行账号，不该落到磁盘上
 * （见 PRODUCT.md §6.1）。重新验证时表单内容靠 React state 保住，不需要经过存储。
 */

/** 恢复窗口：刷新后多久之内还能免验证接着填。 */
export const RESUME_WINDOW_MS = 10 * 60 * 1000;

const PREFIX = 'hipay.collect.';

export interface CollectSession {
  collectToken: string;
  /** 服务端给的令牌到期时刻（ISO）。生死由它说了算，恢复窗口只是额外再紧一道。 */
  expiresAt: string;
}

interface Stored extends CollectSession {
  /** 存下来的时刻，用于算恢复窗口。 */
  savedAt: number;
}

/** 🔴 按链接 token 分开存：A 链接换来的凭据不能拿去填 B（PRODUCT.md §6.2 同理）。 */
const keyOf = (linkToken: string) => `${PREFIX}${linkToken}`;

export function saveSession(linkToken: string, session: CollectSession, now = Date.now()): void {
  try {
    const stored: Stored = { ...session, savedAt: now };
    window.sessionStorage.setItem(keyOf(linkToken), JSON.stringify(stored));
  } catch {
    // 无痕模式 / 禁用站点数据时会直接抛。存不下就退化成「每次都要验证」，不影响正确性。
  }
}

/**
 * 取回还能用的会话，取不到回 null。
 *
 * 两道时间都要过：恢复窗口（这台浏览器能不能免验证接着填）和令牌自己的 `expiresAt`
 * （服务端认不认）。🔴 少判后者的话，人会拿着一个已经废掉的令牌填完整张表，
 * 最后在提交那一刻才被拒 —— 那正是这次要修掉的事。
 */
export function loadSession(linkToken: string, now = Date.now()): CollectSession | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(keyOf(linkToken));
  } catch {
    return null;
  }
  if (!raw) return null;

  let stored: Stored;
  try {
    stored = JSON.parse(raw) as Stored;
  } catch {
    clearSession(linkToken);
    return null;
  }

  const expiresAtMs = Date.parse(stored.expiresAt);
  const usable =
    typeof stored.collectToken === 'string' &&
    stored.collectToken !== '' &&
    Number.isFinite(stored.savedAt) &&
    Number.isFinite(expiresAtMs) &&
    now - stored.savedAt < RESUME_WINDOW_MS &&
    now < expiresAtMs;

  if (!usable) {
    clearSession(linkToken);
    return null;
  }
  return { collectToken: stored.collectToken, expiresAt: stored.expiresAt };
}

export function clearSession(linkToken: string): void {
  try {
    window.sessionStorage.removeItem(keyOf(linkToken));
  } catch {
    // 同 saveSession：存储不可用时什么都不用做
  }
}
